// src/pages/cashier/CashierDashboard.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Tooltip,
  Paper,
  LinearProgress,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  RestaurantMenu as MenuIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  TableRestaurant as TableRestaurantIcon,
  Send as SendIcon,
  Print as PrintIcon,
  Kitchen as KitchenIcon,
  LocalBar as LocalBarIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Cancel as CancelIcon,
  Done as DoneIcon,
  Pending as PendingIcon,
  HourglassTop as HourglassIcon,
  ErrorOutline as ErrorIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  ReceiptLong as ReceiptLongIcon,
  Receipt as ReceiptOutlinedIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/cashier/Header';
import Footer from '../../../components/RestaurantPOS/waiters/Footer';
import Preloader from '../../../theme/Preloader';
import restaurantConfig from '../../../config/Organisation_Details';
import '../../../styles/Waiters.css';
import '../../../styles/ThermalReceipt.css';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 10;
const REFRESH_INTERVAL = 30000;

// Payment Methods with their database IDs
const PAYMENT_METHOD_IDS = {
  CASH: 1,
  CARD: 2,
  BANK_TRANSFER: 3,
  MOBILE_MONEY: 4,
  COMPLIMENTARY: 5,
};

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash', icon: MoneyIcon, color: '#4CAF50' },
  { value: 'CARD', label: 'Card', icon: CreditCardIcon, color: '#1976D2' },
  { value: 'BANK_TRANSFER', label: 'Transfer', icon: BankIcon, color: '#9C27B0' },
  { value: 'MIXED', label: 'Mixed', icon: PaymentIcon, color: '#FF6F00' },
];

// Order Status Colors
const ORDER_STATUS_COLORS = {
  PENDING: { bg: '#FFF3E0', color: '#E65100' },
  IN_PROGRESS: { bg: '#E3F2FD', color: '#0D47A1' },
  READY: { bg: '#E8F5E9', color: '#1B5E20' },
  COMPLETED: { bg: '#F3E5F5', color: '#4A148C' },
  CANCELLED: { bg: '#FFEBEE', color: '#B71C1C' },
};

const PAYMENT_STATUS_COLORS = {
  UNPAID: { bg: '#FFEBEE', color: '#C62828' },
  PAID: { bg: '#E8F5E9', color: '#2E7D32' },
  PARTIAL: { bg: '#FFF3E0', color: '#E65100' },
  REFUNDED: { bg: '#F3E5F5', color: '#4A148C' },
  COMPLIMENTARY: { bg: '#F3E5F5', color: '#7B1FA2' },
  CANCELLED: { bg: '#FFEBEE', color: '#C62828' }, // ADD THIS
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status, type = 'order') => {
  if (type === 'order') {
    return ORDER_STATUS_COLORS[status] || { bg: '#F5F5F5', color: '#616161' };
  }
  return PAYMENT_STATUS_COLORS[status] || { bg: '#F5F5F5', color: '#616161' };
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[Cashier] Error checking token expiry:', err);
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    console.error('[Cashier] Error getting token expiry:', err);
    return 0;
  }
};

// ============================================
// API HELPERS
// ============================================

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[Cashier] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[Cashier] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[Cashier] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[Cashier] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[Cashier] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const url = `${FULL_API_URL}${endpoint}`;
  console.debug(`[Cashier] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[Cashier] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[Cashier] API Error:', err.message);
    throw err;
  }
};

// ============================================
// LOCATION CONFIGURATION
// ============================================

const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    accentColor: '#C62828',
    lightColor: '#4CAF50',
    icon: '🌍',
    path: '/african-cashier'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    accentColor: '#C62828',
    lightColor: '#1976D2',
    icon: '🌐',
    path: '/continental-cashier'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    accentColor: '#C62828',
    lightColor: '#9C27B0',
    icon: '🍸',
    path: '/lounge-cashier'
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const CashierDashboard = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get location config
  const locationConfig = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african;
  const LOCATION_COLOR = locationConfig.color;
  const ACCENT_COLOR = locationConfig.accentColor;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('orders');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [displayText, setDisplayText] = useState('');
const [lastEditedField, setLastEditedField] = useState(null);
  // Data states
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
 const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  
  // Bill Dialog states
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [billData, setBillData] = useState(null);
  const [isBillPrinting, setIsBillPrinting] = useState(false);
  
  // Payment Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [cashAmount, setCashAmount] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  
  // Order Detail Dialog
  const [orderDetailDialogOpen, setOrderDetailDialogOpen] = useState(false);
  
  // Receipt Dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
  
  // Stats Dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================
  // REFS
  // ============================================
  
  const typingTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const printWindowRef = useRef(null);

  // ============================================
  // CONFIG
  // ============================================
  
  const fullText = `${LOCATION_NAME} Cashier`;
  const currency = restaurantConfig.currency || '₦';
  const cashierId = parseInt(localStorage.getItem('user_id')) || null;

  // ============================================
  // TYPING EFFECT
  // ============================================
  
  useEffect(() => {
    if (isLoading) return;
    
    let index = 0;
    let isDeleting = false;
    
    const typeText = () => {
      if (!isMountedRef.current) return;
      
      if (!isDeleting) {
        if (index <= fullText.length) {
          setDisplayText(fullText.substring(0, index));
          index++;
          typingTimeoutRef.current = setTimeout(typeText, 120);
        } else {
          typingTimeoutRef.current = setTimeout(() => {
            isDeleting = true;
            index = fullText.length;
            typingTimeoutRef.current = setTimeout(typeText, 50);
          }, 3000);
        }
      } else {
        if (index >= 0) {
          setDisplayText(fullText.substring(0, index));
          index--;
          typingTimeoutRef.current = setTimeout(typeText, 50);
        } else {
          isDeleting = false;
          index = 0;
          typingTimeoutRef.current = setTimeout(typeText, 500);
        }
      }
    };
    
    typingTimeoutRef.current = setTimeout(typeText, 500);
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [isLoading, fullText]);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchOrders = useCallback(async () => {
  try {
    console.debug(`[Cashier] Fetching orders for ${LOCATION_NAME}...`);
    setIsFetching(true);
    
    const params = new URLSearchParams();
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    params.append('location_id', LOCATION_ID);
    
    // Add order status filter if not ALL
    if (selectedStatus !== 'ALL') {
      params.append('order_status', selectedStatus);
    }
    
    // Add payment status filter if not ALL
    if (selectedPaymentStatus !== 'ALL') {
      params.append('payment_status', selectedPaymentStatus);
    }
    
    // Get today's date for filtering
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    params.append('date_from', dateStr);
    params.append('date_to', dateStr);
    
    // Add search term if present
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    // IMPORTANT: Use history endpoint to include cancelled orders
    // The history endpoint returns all orders regardless of is_active status
    const url = `/orders/history?${params.toString()}`;
    const data = await apiRequest(url);
    
    console.debug('[Cashier] Orders fetched:', data);
    
    if (isMountedRef.current) {
      const ordersData = data.items || [];
      setOrders(ordersData);
      setFilteredOrders(ordersData);
      calculateStats(ordersData);
    }
  } catch (err) {
    console.error('[Cashier] Error fetching orders:', err);
    if (isMountedRef.current) {
      setSnackbar({
        open: true,
        message: `Failed to fetch orders: ${err.message}`,
        severity: 'error',
      });
    }
  } finally {
    if (isMountedRef.current) {
      setIsFetching(false);
    }
  }
}, [LOCATION_ID, selectedStatus, selectedPaymentStatus, searchTerm, page, rowsPerPage, LOCATION_NAME]);


const fetchOrderDetails = useCallback(async (orderId) => {
    try {
      console.debug(`[Cashier] Fetching order details for ID: ${orderId}`);
      const data = await apiRequest(`/orders/${orderId}`);
      console.debug('[Cashier] Order details fetched:', data);
      return data;
    } catch (err) {
      console.error('[Cashier] Error fetching order details:', err);
      throw err;
    }
  }, []);
  
 const calculateStats = useCallback((ordersData) => {
  const totalOrders = ordersData.length;
  
  // Calculate revenue excluding complimentary and cancelled orders
  const totalAmount = ordersData.reduce((sum, order) => {
    // Skip cancelled orders
    if (order.order_status === 'CANCELLED') return sum;
    
    // Skip complimentary orders
    if (order.payment_status === 'COMPLIMENTARY' || order.is_complimentary) return sum;
    
    // Use total_amount (which already includes discount)
    return sum + (order.total_amount || 0);
  }, 0);
  
  const unpaidOrders = ordersData.filter(o => o.payment_status === 'UNPAID').length;
  const paidOrders = ordersData.filter(o => o.payment_status === 'PAID').length;
  const pendingOrders = ordersData.filter(o => o.order_status === 'PENDING').length;
  
  // Completed orders - exclude CANCELLED and COMPLIMENTARY from revenue stats
  const completedOrders = ordersData.filter(o => 
    o.order_status === 'COMPLETED' && 
    o.payment_status !== 'COMPLIMENTARY' &&
    o.payment_status !== 'CANCELLED'
  ).length;
  
  const partialOrders = ordersData.filter(o => o.payment_status === 'PARTIAL').length;
  
  setStats({
    total_orders: totalOrders,
    total_revenue: totalAmount,
    unpaid_orders: unpaidOrders,
    paid_orders: paidOrders,
    pending_orders: pendingOrders,
    completed_orders: completedOrders,
    partial_orders: partialOrders,
    today_orders: totalOrders,
    today_revenue: totalAmount,
  });
}, []);
  // ============================================
  // INITIALIZATION
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug(`[Cashier] Initializing for ${LOCATION_NAME}...`);
    
    fetchOrders();
    
    refreshIntervalRef.current = setInterval(() => {
      if (isMountedRef.current && !isFetching) {
        console.debug('[Cashier] Auto-refreshing orders...');
        fetchOrders();
      }
    }, REFRESH_INTERVAL);
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 1000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(refreshIntervalRef.current);
      isMountedRef.current = false;
    };
  }, [fetchOrders, LOCATION_NAME]);
  
  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================
  
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      
      if (timeLeft > 0 && timeLeft < 120000) {
        console.debug('[Cashier] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };
    
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ============================================
  // FILTERING
  // ============================================
  
  const applyFilters = useCallback(() => {
    let filtered = [...orders];
    
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(order => {
        return (
          order.order_number?.toLowerCase().includes(term) ||
          order.customer_name?.toLowerCase().includes(term) ||
          order.customer_phone?.includes(term)
        );
      });
    }
    
    setFilteredOrders(filtered);
  }, [orders, searchTerm]);
  
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      applyFilters();
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [applyFilters, searchTerm]);

  // ============================================
  // PRINT FUNCTIONS
  // ============================================
  
  const openPrintWindow = useCallback((htmlContent, title) => {
    if (printWindowRef.current && !printWindowRef.current.closed) {
      printWindowRef.current.close();
    }
    
    const printWindow = window.open('', '_blank', 'width=400,height=600,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
    
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: 'Please allow popups for printing',
        severity: 'warning',
      });
      return null;
    }
    
    printWindowRef.current = printWindow;
    
    const allStyles = document.querySelectorAll('style');
    let combinedStyles = '';
    allStyles.forEach(style => {
      combinedStyles += style.innerHTML;
    });
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <style>
            ${combinedStyles}
            @media print {
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { margin: 0; padding: 0; background: white; }
              .no-print { display: none !important; }
              #print-content { max-width: 80mm; margin: 0 auto; padding: 8px 6px; }
            }
            body {
              background: #f0f0f0;
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            #print-content {
              max-width: 80mm;
              margin: 0 auto;
              background: white;
              padding: 10px 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
              border-radius: 4px;
            }
            .print-instructions {
              text-align: center;
              padding: 10px;
              margin-bottom: 10px;
              background: #e3f2fd;
              border-radius: 4px;
              font-family: Arial, sans-serif;
              font-size: 12px;
              max-width: 80mm;
            }
            .print-button {
              display: block;
              width: 100%;
              max-width: 80mm;
              padding: 12px;
              margin-top: 10px;
              background: #1976D2;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              font-weight: bold;
              cursor: pointer;
            }
            .print-button:hover {
              background: #1565C0;
            }
          </style>
        </head>
        <body>
          <div class="print-instructions no-print">
            🖨️ Click the "Print" button below to print
            <br>
            <small>${title}</small>
          </div>
          <div id="print-content">
            ${htmlContent}
          </div>
          <button class="print-button no-print" onclick="window.print(); setTimeout(function() { window.close(); }, 3000);">
            🖨️ Print ${title}
          </button>
          <script>
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 3000);
            }, 500);
          <\/script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    return printWindow;
  }, []);

  // ============================================
  // BILL HANDLING
  // ============================================
  
  const generateBillHTML = useCallback((order) => {
    if (!order) return '';
    
    const ticketTotal = order.total_amount || 0;
    const currencySymbol = '₦';
    const formatCurrencyLocal = (amount) => {
      return `${currencySymbol} ${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };
    
    const formatDateLocal = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };
    
    const formatTimeLocal = (timeString) => {
      if (!timeString) return 'N/A';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    };
    
    const waiterName = order.waiter_name || localStorage.getItem('user_name') || 'Waiter';
    const tableName = order.table_name || 'N/A';
    const orderType = order.order_type || 'DINE_IN';
    const paymentStatus = order.payment_status || 'UNPAID';
    
    return `
      <div style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:10px;line-height:1.6;color:#000000;font-weight:bold;">
        <!-- Header -->
        <div style="text-align:center;padding-bottom:10px;border-bottom:3px solid #000;margin-bottom:10px;">
          <div style="font-size:18px;font-weight:bold;letter-spacing:2px;color:#000;">${restaurantConfig.name || 'RESTAURANT'}</div>
          ${restaurantConfig.address ? `<div style="font-size:8px;font-weight:bold;color:#333;">${restaurantConfig.address}</div>` : ''}
          ${restaurantConfig.phone ? `<div style="font-size:8px;font-weight:bold;color:#333;">Tel: ${restaurantConfig.phone}</div>` : ''}
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:14px;font-weight:bold;letter-spacing:2px;color:#000;">CUSTOMER BILL</div>
          <div style="font-size:9px;font-weight:bold;color:#333;margin-top:3px;">${formatDateLocal(order.order_time)} ${formatTimeLocal(order.order_time)}</div>
        </div>
        
        <!-- Order Info -->
        <div style="margin-bottom:6px;padding:4px 2px;background:#f5f5f5;border-radius:4px;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>ORDER: ${order.order_number}</span>
            <span>#${String(order.serial_number || 0).padStart(6, '0')}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Table: ${tableName}</span>
            <span>${orderType}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Waiter: ${waiterName}</span>
            <span>${order.location_name || LOCATION_NAME}</span>
          </div>
          ${order.customer_name ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>Customer: ${order.customer_name}</span>
              ${order.customer_phone ? `<span>${order.customer_phone}</span>` : ''}
            </div>
          ` : ''}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        
        <!-- Items -->
        <div style="margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;padding:4px 2px;border-bottom:2px solid #000;font-weight:bold;font-size:8px;background-color:#e0e0e0;color:#000;">
            <span style="font-size:8px;font-weight:bold;flex:1;">ITEM</span>
            <span style="font-size:8px;font-weight:bold;min-width:25px;text-align:center;">QTY</span>
            <span style="font-size:8px;font-weight:bold;min-width:65px;text-align:right;">TOTAL</span>
          </div>
          
          ${(order.items || []).map((item, idx) => {
            const quantity = item.quantity || 1;
            const name = item.name || 'Item';
            const price = item.price || item.amount || 0;
            const total = price * quantity;
            
            return `
              <div style="margin-bottom:2px;" key="${idx}">
                <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:9px;font-weight:bold;border-bottom:1px solid #ccc;color:#000;">
                  <span style="font-size:9px;font-weight:bold;flex:1;">${name}</span>
                  <span style="font-size:9px;font-weight:bold;min-width:25px;text-align:center;">${quantity}</span>
                  <span style="font-size:9px;font-weight:bold;min-width:65px;text-align:right;">${formatCurrencyLocal(total)}</span>
                </div>
                ${item.category ? `
                  <div style="padding-left:4px;margin-bottom:2px;font-size:7px;color:#666;font-weight:bold;">
                    ${item.category}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        
        <!-- Totals -->
        <div style="padding:4px 0;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>SUBTOTAL</span>
            <span>${formatCurrencyLocal(order.subtotal || 0)}</span>
          </div>
          ${order.tax > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>TAX</span>
              <span>${formatCurrencyLocal(order.tax)}</span>
            </div>
          ` : ''}
          ${order.discount > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>DISCOUNT</span>
              <span>-${formatCurrencyLocal(order.discount)}</span>
            </div>
          ` : ''}
        </div>
        
        <div style="border-top:3px double #000;margin:4px 0;"></div>
        
        <div style="display:flex;justify-content:space-between;padding:6px 4px;font-size:14px;font-weight:bold;color:#000;background:#f5f5f5;border-radius:4px;">
          <span>TOTAL</span>
          <span>${formatCurrencyLocal(ticketTotal)}</span>
        </div>
        
        ${order.notes ? `
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="margin-bottom:4px;padding:4px;background:#fff8e1;border-radius:4px;">
            <div style="font-size:7px;color:#555;text-transform:uppercase;font-weight:bold;">Notes</div>
            <div style="font-size:9px;color:#000;font-weight:bold;">${order.notes}</div>
          </div>
        ` : ''}
        
        <!-- Payment Status -->
        <div style="border-top:1px dashed #000;margin:6px 0;"></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:10px;font-weight:bold;color:#000;">
          <span>PAYMENT STATUS</span>
          <span style="color:${paymentStatus === 'PAID' ? '#2E7D32' : '#C62828'};">${paymentStatus}</span>
        </div>
        
        <!-- Footer -->
        <div style="text-align:center;padding-top:10px;border-top:3px solid #000;margin-top:10px;">
          <div style="font-size:10px;font-weight:bold;color:#000;">Thank you for your patronage!</div>
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:6px;color:#666;margin-top:2px;font-weight:bold;">
            ${restaurantConfig.name || 'Restaurant'} • Customer Bill
          </div>
          <div style="font-size:6px;color:#999;margin-top:2px;font-weight:bold;">
            Please present this bill at the cashier for payment
          </div>
        </div>
      </div>
    `;
  }, [LOCATION_NAME]);

  const handlePrintBill = useCallback(async (order) => {
    try {
      console.debug(`[Cashier] Printing bill for order: ${order.order_number}`);
      setIsBillPrinting(true);
      
      const details = order.items ? order : await fetchOrderDetails(order.id);
      setBillData(details);
      setBillDialogOpen(true);
      
      const billHTML = generateBillHTML(details);
      const printWindow = openPrintWindow(billHTML, `Bill - ${order.order_number}`);
      
      if (printWindow) {
        setSnackbar({
          open: true,
          message: `Bill for ${order.order_number} sent to printer!`,
          severity: 'success',
        });
      }
      
    } catch (err) {
      console.error('[Cashier] Error printing bill:', err);
      setSnackbar({
        open: true,
        message: `Failed to print bill: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTimeout(() => {
        setIsBillPrinting(false);
      }, 1000);
    }
  }, [fetchOrderDetails, generateBillHTML, openPrintWindow]);

  const handleCloseBillDialog = useCallback(() => {
    setBillDialogOpen(false);
    setBillData(null);
    setIsBillPrinting(false);
  }, []);

  // ============================================
  // PAYMENT HANDLING
  // ============================================
  
  const handleOpenPaymentDialog = useCallback(async (order) => {
    console.debug(`[Cashier] Opening payment dialog for order: ${order.order_number}`);
    setSelectedOrder(order);
    setPaymentAmount(order.total_amount?.toString() || '');
    setPaymentMethod('CASH');
    setSelectedPaymentMethod('CASH');
    setCashAmount(order.total_amount?.toString() || '');
    setTransferAmount('0');
    setCardAmount('0');
    setPaymentSuccess(false);
    setPaymentDialogOpen(true);
  }, []);
  
  const handleClosePaymentDialog = useCallback(() => {
    setPaymentDialogOpen(false);
    setSelectedOrder(null);
    setPaymentAmount('');
    setCashAmount('');
    setTransferAmount('');
    setCardAmount('');
    setPaymentSuccess(false);
    setIsProcessingPayment(false);
  }, []);
  
  const handlePaymentMethodChange = useCallback((method) => {
    setPaymentMethod(method);
    setSelectedPaymentMethod(method);
    if (method === 'CASH') {
      setCashAmount(paymentAmount);
      setTransferAmount('0');
      setCardAmount('0');
    } else if (method === 'CARD') {
      setCardAmount(paymentAmount);
      setCashAmount('0');
      setTransferAmount('0');
    } else if (method === 'BANK_TRANSFER') {
      setTransferAmount(paymentAmount);
      setCashAmount('0');
      setCardAmount('0');
    } else if (method === 'MIXED') {
      const total = parseFloat(paymentAmount) || 0;
      const third = (total / 3).toFixed(2);
      setCashAmount(third);
      setTransferAmount(third);
      setCardAmount(third);
    }
  }, [paymentAmount]);
  
 // In CashierDashboard.jsx - Updated handleProcessPayment

const handleProcessPayment = useCallback(async () => {
  if (!selectedOrder) {
    setSnackbar({
      open: true,
      message: 'No order selected for payment',
      severity: 'error',
    });
    return;
  }
  
  const totalAmount = parseFloat(paymentAmount);
  if (isNaN(totalAmount) || totalAmount <= 0) {
    setSnackbar({
      open: true,
      message: 'Please enter a valid payment amount',
      severity: 'warning',
    });
    return;
  }
  
  if (totalAmount > selectedOrder.total_amount) {
    setSnackbar({
      open: true,
      message: `Payment amount exceeds order total`,
      severity: 'warning',
    });
    return;
  }
  
  // Build payments array for the new backend
  let payments = [];
  
  if (paymentMethod === 'MIXED') {
    const cash = parseFloat(cashAmount) || 0;
    const transfer = parseFloat(transferAmount) || 0;
    const card = parseFloat(cardAmount) || 0;
    const total = cash + transfer + card;
    
    if (Math.abs(total - totalAmount) > 0.01) {
      setSnackbar({
        open: true,
        message: `Mixed payment total (${formatCurrency(total)}) does not match payment amount (${formatCurrency(totalAmount)})`,
        severity: 'warning',
      });
      return;
    }
    
    if (cash > 0) {
      payments.push({
        payment_method_id: PAYMENT_METHOD_IDS.CASH,
        amount: cash,
        notes: 'Cash payment',
      });
    }
    if (transfer > 0) {
      payments.push({
        payment_method_id: PAYMENT_METHOD_IDS.BANK_TRANSFER,
        amount: transfer,
        notes: 'Bank transfer payment',
      });
    }
    if (card > 0) {
      payments.push({
        payment_method_id: PAYMENT_METHOD_IDS.CARD,
        amount: card,
        notes: 'Card payment',
      });
    }
  } else {
    const methodId = PAYMENT_METHOD_IDS[paymentMethod] || PAYMENT_METHOD_IDS.CASH;
    payments.push({
      payment_method_id: methodId,
      amount: totalAmount,
      notes: `${paymentMethod} payment`,
    });
  }
  
  setIsProcessingPayment(true);
  
  try {
    // Get the current user ID from localStorage
    const userId = localStorage.getItem('user_id');
    const userRole = localStorage.getItem('user_role')?.toLowerCase();
    
    // Parse user ID as integer (or null if not available)
    const cashierId = userId ? parseInt(userId, 10) : null;
    
    console.debug('[Cashier] Processing payment:', {
      order_id: selectedOrder.id,
      order_number: selectedOrder.order_number,
      total_amount: totalAmount,
      payments: payments,
      cashier_id: cashierId,
      user_role: userRole,
    });
    
    const isFullyPaid = totalAmount >= selectedOrder.total_amount;
    const paymentStatus = isFullyPaid ? 'PAID' : 'PARTIAL';
    
    const paymentData = {
      payment_status: paymentStatus,
      payments: payments,
      cashier_id: cashierId,  // Send the cashier ID
    };
    
    console.debug('[Cashier] Sending payment data:', JSON.stringify(paymentData, null, 2));
    
    const response = await apiRequest(`/orders/${selectedOrder.id}/payment`, {
      method: 'PATCH',
      body: JSON.stringify(paymentData),
    });
    
    console.debug('[Cashier] Payment processed successfully', response);
    
    // Check if cashier_id was set in the response
    if (response && response.cashier_id) {
      console.debug(`[Cashier] ✅ Cashier ID ${response.cashier_id} recorded for order ${selectedOrder.order_number}`);
    } else {
      console.warn(`[Cashier] ⚠️ Cashier ID not found in response for order ${selectedOrder.order_number}`);
    }
    
    setPaymentSuccess(true);
    setSelectedPaymentMethod(paymentMethod);
    
    setSnackbar({
      open: true,
      message: `Payment of ${formatCurrency(totalAmount)} processed successfully for order ${selectedOrder.order_number}`,
      severity: 'success',
    });
    
    if (isFullyPaid) {
      const updatedOrder = await fetchOrderDetails(selectedOrder.id);
      setReceiptData(updatedOrder);
      setReceiptDialogOpen(true);
      setSnackbar({
        open: true,
        message: `Payment complete! Receipt ready for printing.`,
        severity: 'success',
      });
    }
    
    await fetchOrders();
    
    setTimeout(() => {
      handleClosePaymentDialog();
    }, 1500);
    
  } catch (err) {
    console.error('[Cashier] Error processing payment:', err);
    setSnackbar({
      open: true,
      message: `Payment failed: ${err.message}`,
      severity: 'error',
    });
  } finally {
    setIsProcessingPayment(false);
  }
}, [
  selectedOrder, 
  paymentAmount, 
  paymentMethod, 
  cashAmount, 
  transferAmount, 
  cardAmount, 
  handleClosePaymentDialog, 
  fetchOrders, 
  fetchOrderDetails
]);
  // ============================================
  // ORDER DETAIL HANDLING
  // ============================================
  
  const handleViewOrderDetails = useCallback(async (order) => {
    try {
      console.debug(`[Cashier] Viewing order details for: ${order.order_number}`);
      const details = await fetchOrderDetails(order.id);
      setOrderDetails(details);
      setOrderDetailDialogOpen(true);
    } catch (err) {
      console.error('[Cashier] Error fetching order details:', err);
      setSnackbar({
        open: true,
        message: `Failed to load order details: ${err.message}`,
        severity: 'error',
      });
    }
  }, [fetchOrderDetails]);
  
  const handleCloseOrderDetails = useCallback(() => {
    setOrderDetailDialogOpen(false);
    setOrderDetails(null);
  }, []);

  // ============================================
  // RECEIPT HANDLING
  // ============================================
  
  const generateThermalReceipt = useCallback((order) => {
    if (!order) return '';
    
    const ticketTotal = order.total_amount || 0;
    const currencySymbol = '₦';
    const formatCurrencyLocal = (amount) => {
      return `${currencySymbol} ${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };
    
    const formatDateLocal = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };
    
    const formatTimeLocal = (timeString) => {
      if (!timeString) return 'N/A';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    };
    
    const waiterName = order.waiter_name || localStorage.getItem('user_name') || 'Waiter';
    const tableName = order.table_name || 'N/A';
    const orderType = order.order_type || 'DINE_IN';
    const paymentStatus = order.payment_status || 'UNPAID';
    
    // Build payment breakdown from order payments
    let paymentBreakdown = '';
    if (order.payments && order.payments.length > 0) {
      paymentBreakdown = order.payments.map(p => `
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>${p.payment_method?.name || 'Payment'}</span>
          <span>${formatCurrencyLocal(p.amount)}</span>
        </div>
      `).join('');
    }
    
    return `
      <div style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:10px;line-height:1.6;color:#000000;font-weight:bold;">
        <!-- Header -->
        <div style="text-align:center;padding-bottom:10px;border-bottom:3px solid #000;margin-bottom:10px;">
          <div style="font-size:18px;font-weight:bold;letter-spacing:2px;color:#000;">${restaurantConfig.name || 'RESTAURANT'}</div>
          ${restaurantConfig.address ? `<div style="font-size:8px;font-weight:bold;color:#333;">${restaurantConfig.address}</div>` : ''}
          ${restaurantConfig.phone ? `<div style="font-size:8px;font-weight:bold;color:#333;">Tel: ${restaurantConfig.phone}</div>` : ''}
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:14px;font-weight:bold;letter-spacing:2px;color:#000;">PAYMENT RECEIPT</div>
          <div style="font-size:9px;font-weight:bold;color:#333;margin-top:3px;">${formatDateLocal(order.order_time)} ${formatTimeLocal(order.order_time)}</div>
        </div>
        
        <!-- Order Info -->
        <div style="margin-bottom:6px;padding:4px 2px;background:#f5f5f5;border-radius:4px;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>ORDER: ${order.order_number}</span>
            <span>#${String(order.serial_number || 0).padStart(6, '0')}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Table: ${tableName}</span>
            <span>${orderType}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Waiter: ${waiterName}</span>
            <span>${order.location_name || LOCATION_NAME}</span>
          </div>
          ${order.customer_name ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>Customer: ${order.customer_name}</span>
              ${order.customer_phone ? `<span>${order.customer_phone}</span>` : ''}
            </div>
          ` : ''}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        
        <!-- Items -->
        <div style="margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;padding:4px 2px;border-bottom:2px solid #000;font-weight:bold;font-size:8px;background-color:#e0e0e0;color:#000;">
            <span style="font-size:8px;font-weight:bold;flex:1;">ITEM</span>
            <span style="font-size:8px;font-weight:bold;min-width:25px;text-align:center;">QTY</span>
            <span style="font-size:8px;font-weight:bold;min-width:65px;text-align:right;">TOTAL</span>
          </div>
          
          ${(order.items || []).map((item, idx) => {
            const quantity = item.quantity || 1;
            const name = item.name || 'Item';
            const price = item.price || item.amount || 0;
            const total = price * quantity;
            
            return `
              <div style="margin-bottom:2px;" key="${idx}">
                <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:9px;font-weight:bold;border-bottom:1px solid #ccc;color:#000;">
                  <span style="font-size:9px;font-weight:bold;flex:1;">${name}</span>
                  <span style="font-size:9px;font-weight:bold;min-width:25px;text-align:center;">${quantity}</span>
                  <span style="font-size:9px;font-weight:bold;min-width:65px;text-align:right;">${formatCurrencyLocal(total)}</span>
                </div>
                ${item.category ? `
                  <div style="padding-left:4px;margin-bottom:2px;font-size:7px;color:#666;font-weight:bold;">
                    ${item.category}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        
        <!-- Totals -->
        <div style="padding:4px 0;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>SUBTOTAL</span>
            <span>${formatCurrencyLocal(order.subtotal || 0)}</span>
          </div>
          ${order.tax > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>TAX</span>
              <span>${formatCurrencyLocal(order.tax)}</span>
            </div>
          ` : ''}
          ${order.discount > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
              <span>DISCOUNT</span>
              <span>-${formatCurrencyLocal(order.discount)}</span>
            </div>
          ` : ''}
        </div>
        
        <div style="border-top:3px double #000;margin:4px 0;"></div>
        
        <div style="display:flex;justify-content:space-between;padding:6px 4px;font-size:14px;font-weight:bold;color:#000;background:#f5f5f5;border-radius:4px;">
          <span>TOTAL</span>
          <span>${formatCurrencyLocal(ticketTotal)}</span>
        </div>
        
        <!-- Payment Details -->
        <div style="border-top:1px dashed #000;margin:6px 0;"></div>
        <div style="padding:4px 0;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>PAYMENT STATUS</span>
            <span style="color:${paymentStatus === 'PAID' ? '#2E7D32' : '#C62828'};">${paymentStatus}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>TOTAL PAID</span>
            <span>${formatCurrencyLocal(order.total_paid || 0)}</span>
          </div>
          ${order.balance > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#C62828;">
              <span>BALANCE</span>
              <span>${formatCurrencyLocal(order.balance)}</span>
            </div>
          ` : ''}
          ${paymentBreakdown ? `
            <div style="border-top:1px dashed #000;margin:4px 0;"></div>
            <div style="font-size:8px;font-weight:bold;color:#333;margin-bottom:2px;">PAYMENT BREAKDOWN</div>
            ${paymentBreakdown}
          ` : ''}
        </div>
        
        ${order.notes ? `
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="margin-bottom:4px;padding:4px;background:#fff8e1;border-radius:4px;">
            <div style="font-size:7px;color:#555;text-transform:uppercase;font-weight:bold;">Notes</div>
            <div style="font-size:9px;color:#000;font-weight:bold;">${order.notes}</div>
          </div>
        ` : ''}
        
        <!-- Footer -->
        <div style="text-align:center;padding-top:10px;border-top:3px solid #000;margin-top:10px;">
          <div style="font-size:10px;font-weight:bold;color:#000;">Thank you for your patronage!</div>
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:6px;color:#666;margin-top:2px;font-weight:bold;">
            ${restaurantConfig.name || 'Restaurant'} • Payment Receipt
          </div>
        </div>
      </div>
    `;
  }, [LOCATION_NAME]);

 const handlePrintReceipt = useCallback(async (order) => {
  try {
    console.debug(`[Cashier] Printing receipt for order: ${order.order_number}`);
    setIsReceiptPrinting(true);
    
    const details = order.items ? order : await fetchOrderDetails(order.id);
    
    // Allow printing for PAID, REFUNDED, and COMPLIMENTARY orders
    if (details.payment_status !== 'PAID' && details.payment_status !== 'REFUNDED' && details.payment_status !== 'COMPLIMENTARY') {
      setSnackbar({
        open: true,
        message: 'This order has not been paid yet. Please process payment first.',
        severity: 'warning',
      });
      setIsReceiptPrinting(false);
      return;
    }
  
      const receiptHTML = generateThermalReceipt(details);
      const printWindow = openPrintWindow(receiptHTML, `Receipt - ${order.order_number}`);
      
      if (printWindow) {
        setSnackbar({
          open: true,
          message: `Receipt for ${order.order_number} sent to printer!`,
          severity: 'success',
        });
      }
      
    } catch (err) {
      console.error('[Cashier] Error printing receipt:', err);
      setSnackbar({
        open: true,
        message: `Failed to print receipt: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTimeout(() => {
        setIsReceiptPrinting(false);
      }, 1000);
    }
  }, [fetchOrderDetails, generateThermalReceipt, openPrintWindow]);
  
  const handleCloseReceiptDialog = useCallback(() => {
    setReceiptDialogOpen(false);
    setReceiptData(null);
    setIsReceiptPrinting(false);
  }, []);

  // ============================================
// RENDER HELPERS
// ============================================

const getStatusChip = useCallback((status, type = 'order') => {
  const colors = getStatusColor(status, type);
  const label = status?.replace('_', ' ') || 'Unknown';
  
  let chipSx = {
    bgcolor: colors.bg,
    color: colors.color,
    fontWeight: 700,
    fontSize: { xs: '0.6rem', sm: '0.75rem' },
    height: { xs: 22, sm: 26 },
    '& .MuiChip-label': {
      px: { xs: 1, sm: 1.5 },
    },
  };
  
  // Special styling for COMPLIMENTARY
  if (status === 'COMPLIMENTARY') {
    chipSx = {
      ...chipSx,
      bgcolor: '#F3E5F5',
      color: '#7B1FA2',
      border: '1px solid #9C27B0',
    };
  }
  
  // Special styling for CANCELLED
  if (status === 'CANCELLED') {
    chipSx = {
      ...chipSx,
      bgcolor: '#FFEBEE',
      color: '#C62828',
      border: '1px solid #F44336',
    };
  }
  
  return (
    <Chip
      label={label}
      size="small"
      sx={chipSx}
    />
  );
}, []);
 // ============================================
// RENDER HELPERS - FIXED ORDER CARD WITH LARGE VISIBLE TEXT
// ============================================

const renderOrderCard = useCallback((order) => {
  // Check if order is paid, complimentary, or cancelled
  const isPaid = order.payment_status === 'PAID' || 
                 order.payment_status === 'REFUNDED' || 
                 order.payment_status === 'COMPLIMENTARY';
  
  // Check if order is cancelled - these should not show Pay button
  const isCancelled = order.order_status === 'CANCELLED' || order.payment_status === 'CANCELLED';
  
  const isPartial = order.payment_status === 'PARTIAL';
  
  return (
    <Card
      key={order.id}
      sx={{
        mb: 2,
        borderRadius: '12px',
        border: isCancelled 
          ? `1px solid ${alpha('#F44336', 0.3)}` 
          : `1px solid ${alpha(LOCATION_COLOR, 0.12)}`,
        opacity: isCancelled ? 0.75 : 1,
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: isCancelled ? '#F44336' : LOCATION_COLOR,
        },
        transition: 'all 0.2s ease',
      }}
    >
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5 },
        '&:last-child': { pb: { xs: 2, sm: 2.5 } }
      }}>
        <Grid container spacing={1.5} alignItems="center" sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {/* Order Number & Time */}
          <Grid item xs={12} sm={1.5}>
            <Box>
              <Typography variant="body1" fontWeight={700} sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                color: isCancelled ? '#C62828' : 'inherit',
              }}>
                {order.order_number}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatTime(order.order_time)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, display: 'block' }}>
                #{String(order.serial_number || 0).padStart(6, '0')}
              </Typography>
            </Box>
          </Grid>
          
          {/* Location & Table */}
          <Grid item xs={6} sm={1.5}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {order.location_name || LOCATION_NAME}
              </Typography>
              {order.table_name && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' } }}>
                  <TableRestaurantIcon sx={{ fontSize: '0.7rem', mr: 0.3 }} />
                  {order.table_name}
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* Customer */}
          <Grid item xs={6} sm={1.5}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              {order.customer_name || 'Walk-in'}
              {order.customer_phone && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                  📱 {order.customer_phone}
                </Typography>
              )}
            </Typography>
          </Grid>
          
          {/* Items Count */}
          <Grid item xs={3} sm={0.75}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              {order.items?.length || 0} items
            </Typography>
          </Grid>
          
          {/* Total Amount */}
          <Grid item xs={4.5} sm={1.5}>
            <Typography variant="h6" fontWeight={700} color={isCancelled ? '#C62828' : LOCATION_COLOR} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              {isCancelled ? 'CANCELLED' : formatCurrency(order.total_amount)}
            </Typography>
            {isPartial && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                Partial Payment
              </Typography>
            )}
          </Grid>
          
          {/* Statuses */}
          <Grid item xs={4.5} sm={1.5}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {getStatusChip(order.order_status, 'order')}
              {getStatusChip(order.payment_status, 'payment')}
            </Box>
          </Grid>
          
          {/* Order Type */}
          <Grid item xs={3} sm={1}>
            <Chip
              label={order.order_type === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
              size="small"
              sx={{
                bgcolor: order.order_type === 'DINE_IN' ? alpha('#4CAF50', 0.12) : alpha('#1976D2', 0.12),
                color: order.order_type === 'DINE_IN' ? '#2E7D32' : '#1976D2',
                fontWeight: 700,
                fontSize: { xs: '0.55rem', sm: '0.7rem' },
                height: { xs: 22, sm: 26 },
                maxWidth: '100%',
              }}
            />
          </Grid>
          
          {/* Actions - All on one row with large visible buttons */}
          <Grid item xs={12} sm={2.5}>
            <Box sx={{ 
              display: 'flex', 
              gap: 0.75, 
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleViewOrderDetails(order)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  borderRadius: '6px',
                  borderColor: isCancelled ? '#C62828' : LOCATION_COLOR,
                  color: isCancelled ? '#C62828' : LOCATION_COLOR,
                  py: { xs: 0.5, sm: 0.6 },
                  px: { xs: 1, sm: 1.2 },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  minHeight: { xs: 32, sm: 36 },
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: isCancelled ? '#C62828' : LOCATION_COLOR,
                    bgcolor: alpha(isCancelled ? '#C62828' : LOCATION_COLOR, 0.08),
                  },
                }}
              >
                View
              </Button>
              
              {/* Bill Button - Show for all orders except cancelled */}
              {!isCancelled && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handlePrintBill(order)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    borderRadius: '6px',
                    borderColor: '#1976D2',
                    color: '#1976D2',
                    py: { xs: 0.5, sm: 0.6 },
                    px: { xs: 1, sm: 1.2 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    minHeight: { xs: 32, sm: 36 },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#1976D2',
                      bgcolor: alpha('#1976D2', 0.08),
                    },
                  }}
                >
                  Bill
                </Button>
              )}
              
              {/* Pay Button - Show for unpaid and partial orders (NOT for cancelled or complimentary) */}
              {!isPaid && !isCancelled && order.payment_status !== 'COMPLIMENTARY' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleOpenPaymentDialog(order)}
                  sx={{
                    bgcolor: LOCATION_COLOR,
                    '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    py: { xs: 0.5, sm: 0.6 },
                    px: { xs: 1.2, sm: 1.8 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    minHeight: { xs: 32, sm: 36 },
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isPartial ? 'Complete' : 'Pay'}
                </Button>
              )}
              
              {/* Receipt Button - Show for paid or complimentary orders (NOT for cancelled) */}
              {(isPaid || order.payment_status === 'COMPLIMENTARY') && !isCancelled && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handlePrintReceipt(order)}
                  sx={{
                    bgcolor: order.payment_status === 'COMPLIMENTARY' ? '#9C27B0' : '#2E7D32',
                    '&:hover': { 
                      bgcolor: order.payment_status === 'COMPLIMENTARY' ? '#7B1FA2' : '#1B5E20' 
                    },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    py: { xs: 0.5, sm: 0.6 },
                    px: { xs: 1.2, sm: 1.8 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    minHeight: { xs: 32, sm: 36 },
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {order.payment_status === 'COMPLIMENTARY' ? 'Comp. Receipt' : 'Receipt'}
                </Button>
              )}
              
              {/* For cancelled orders, show a "Cancelled" badge instead of buttons */}
              {isCancelled && (
                <Chip
                  label="CANCELLED"
                  size="small"
                  sx={{
                    bgcolor: '#FFEBEE',
                    color: '#C62828',
                    fontWeight: 700,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    height: { xs: 28, sm: 32 },
                    border: '1px solid #F44336',
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}, [LOCATION_COLOR, LOCATION_NAME, getStatusChip, handleViewOrderDetails, handlePrintBill, handleOpenPaymentDialog, handlePrintReceipt, theme]);



// STATS SUMMARY
  // ============================================
  
  const renderStatsSummary = useCallback(() => {
    if (!stats) return null;
    
    const statCards = [
      {
        title: 'Total Orders',
        value: stats.total_orders || 0,
        icon: ReceiptIcon,
        color: '#1976D2',
      },
      {
        title: 'Pending',
        value: stats.pending_orders || 0,
        icon: HourglassIcon,
        color: '#E65100',
      },
      {
        title: 'Unpaid',
        value: stats.unpaid_orders || 0,
        icon: ErrorIcon,
        color: '#C62828',
      },
      {
        title: "Today's Revenue",
        value: formatCurrency(stats.today_revenue || 0),
        icon: TrendingUpIcon,
        color: '#2E7D32',
      },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: '14px',
                border: `1px solid ${alpha(stat.color, 0.15)}`,
                bgcolor: alpha(stat.color, 0.05),
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: theme.shadows[3],
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(stat.color, 0.15),
                  color: stat.color,
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                }}
              >
                <stat.icon sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' } }} />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  {stat.title}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.2rem' } }}>
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }, [stats, theme]);

  // ============================================
  // FILTER BAR
  // ============================================
  
  const renderFilters = useCallback(() => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: '14px',
          border: `1px solid ${alpha('#000', 0.06)}`,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { md: 'center' },
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search by order #, customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: { xs: '100%', md: '200px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'white',
              '& fieldset': {
                borderColor: alpha('#000', 0.12),
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: '130px' } }}>
          <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</InputLabel>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            label="Status"
            sx={{ borderRadius: '10px', bgcolor: 'white' }}
          >
            <MenuItem value="ALL">All Status</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="READY">Ready</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
        </FormControl>
        
       <FormControl size="small" sx={{ minWidth: { xs: '100%', md: '130px' } }}>
  <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Payment</InputLabel>
  <Select
    value={selectedPaymentStatus}
    onChange={(e) => setSelectedPaymentStatus(e.target.value)}
    label="Payment"
    sx={{ borderRadius: '10px', bgcolor: 'white' }}
  >
    <MenuItem value="ALL">All Payment</MenuItem>
    <MenuItem value="UNPAID">Unpaid</MenuItem>
    <MenuItem value="PAID">Paid</MenuItem>
    <MenuItem value="PARTIAL">Partial</MenuItem>
    <MenuItem value="REFUNDED">Refunded</MenuItem>
    <MenuItem value="COMPLIMENTARY">Complimentary</MenuItem>
    <MenuItem value="CANCELLED">Cancelled</MenuItem>  {/* ADD THIS */}
  </Select>
</FormControl>
        
        <Button
          variant="outlined"
          size="small"
          onClick={fetchOrders}
          startIcon={<RefreshIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            borderColor: LOCATION_COLOR,
            color: LOCATION_COLOR,
            px: 2,
            py: 0.75,
            '&:hover': {
              borderColor: LOCATION_COLOR,
              bgcolor: alpha(LOCATION_COLOR, 0.08),
            },
          }}
        >
          Refresh
        </Button>
      </Paper>
    );
  }, [searchTerm, selectedStatus, selectedPaymentStatus, LOCATION_COLOR, fetchOrders]);

  // ============================================
  // BILL DIALOG
  // ============================================
  
  const renderBillDialog = useCallback(() => {
    if (!billData) return null;
    
    const billHTML = generateBillHTML(billData);
    
    return (
      <Dialog
        open={billDialogOpen}
        onClose={handleCloseBillDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
            maxWidth: '420px',
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: '#f8f9fa',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptOutlinedIcon sx={{ color: '#1976D2' }} />
            <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Customer Bill
            </Typography>
          </Box>
          <IconButton onClick={handleCloseBillDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, bgcolor: '#f5f7fa' }}>
          {isBillPrinting && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  Preparing print...
                </Typography>
                <CircularProgress size={20} />
              </Box>
              <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
            </Box>
          )}
          
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'white',
              borderRadius: '8px',
              border: `1px solid ${alpha('#000', 0.06)}`,
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: billHTML }} />
          </Paper>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          bgcolor: '#f8f9fa',
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleCloseBillDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePrintBill(billData)}
            disabled={isBillPrinting}
            startIcon={isBillPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
            sx={{
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
            }}
          >
            {isBillPrinting ? 'Printing...' : 'Print Bill'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [billDialogOpen, billData, isBillPrinting, handleCloseBillDialog, generateBillHTML, handlePrintBill]);

  // ============================================
  // PAYMENT DIALOG
  // ============================================
  
  const renderPaymentDialog = useCallback(() => {
    if (!selectedOrder) return null;
    
    const orderTotal = selectedOrder.total_amount || 0;
    const remainingBalance = orderTotal - (selectedOrder.paid_amount || 0);
    const isMixed = paymentMethod === 'MIXED';
    
    return (
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Process Payment
            </Typography>
          </Box>
          <IconButton onClick={handleClosePaymentDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: alpha(LOCATION_COLOR, 0.05),
              borderRadius: '12px',
              border: `1px solid ${alpha(LOCATION_COLOR, 0.12)}`,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                  Order #
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                  {selectedOrder.order_number}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                  Total Amount
                </Typography>
                <Typography variant="h6" fontWeight={700} color={LOCATION_COLOR} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                  {formatCurrency(orderTotal)}
                </Typography>
              </Grid>
              {selectedOrder.customer_name && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Customer
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {selectedOrder.customer_name}
                    {selectedOrder.customer_phone && ` (${selectedOrder.customer_phone})`}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
          
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
            Payment Method
          </Typography>
          
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {PAYMENT_METHODS.map((method) => (
              <Grid item xs={6} sm={3} key={method.value}>
                <Paper
                  elevation={0}
                  onClick={() => handlePaymentMethodChange(method.value)}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `2px solid ${paymentMethod === method.value ? method.color : alpha('#000', 0.08)}`,
                    bgcolor: paymentMethod === method.value ? alpha(method.color, 0.08) : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <method.icon sx={{ fontSize: '1.8rem', color: method.color }} />
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, mt: 0.5 }}>
                    {method.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            onChange={(e) => {
              setPaymentAmount(e.target.value);
              if (paymentMethod === 'CASH') setCashAmount(e.target.value);
              else if (paymentMethod === 'CARD') setCardAmount(e.target.value);
              else if (paymentMethod === 'BANK_TRANSFER') setTransferAmount(e.target.value);
            }}
            placeholder="Enter amount to collect"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            helperText={`Remaining balance: ${formatCurrency(remainingBalance)}`}
          />
          
          {isMixed && (
            <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#FF6F00', 0.05), borderRadius: '12px', border: `1px solid ${alpha('#FF6F00', 0.15)}` }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: '0.85rem', color: '#FF6F00' }}>
                Split Payment Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cash"
                    type="number"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Transfer"
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Card"
                    type="number"
                    value={cardAmount}
                    onChange={(e) => setCardAmount(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#FF6F00', 0.08), borderRadius: '8px' }}>
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                  Total:
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.9rem', color: '#FF6F00' }}>
                  {formatCurrency((parseFloat(cashAmount) || 0) + (parseFloat(transferAmount) || 0) + (parseFloat(cardAmount) || 0))}
                </Typography>
              </Box>
            </Box>
          )}
          
          {paymentSuccess && (
            <Alert
              severity="success"
              sx={{ mt: 2, borderRadius: '10px' }}
              icon={<CheckCircleIcon />}
            >
              Payment processed successfully!
              {parseFloat(paymentAmount) >= (selectedOrder.total_amount || 0) && ' Receipt ready for printing.'}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleClosePaymentDialog}
            disabled={isProcessingPayment}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessPayment}
            variant="contained"
            disabled={isProcessingPayment || paymentSuccess}
            sx={{
              bgcolor: LOCATION_COLOR,
              '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
              py: 1.25,
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
            }}
            startIcon={isProcessingPayment ? <CircularProgress size={22} color="inherit" /> : <PaymentIcon />}
          >
            {isProcessingPayment ? 'Processing...' : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [
    paymentDialogOpen,
    selectedOrder,
    paymentMethod,
    paymentAmount,
    cashAmount,
    transferAmount,
    cardAmount,
    paymentSuccess,
    isProcessingPayment,
    LOCATION_COLOR,
    handleClosePaymentDialog,
    handleProcessPayment,
    handlePaymentMethodChange,
    theme,
  ]);

  // ============================================
  // ORDER DETAIL DIALOG
  // ============================================
  
  const renderOrderDetailDialog = useCallback(() => {
  if (!orderDetails) return null;
  
  const isPaid = orderDetails.payment_status === 'PAID' || 
                 orderDetails.payment_status === 'REFUNDED' || 
                 orderDetails.payment_status === 'COMPLIMENTARY';
  
  // Check if order is cancelled
  const isCancelled = orderDetails.order_status === 'CANCELLED' || 
                      orderDetails.payment_status === 'CANCELLED';

  return (
    <Dialog
      open={orderDetailDialogOpen}
      onClose={handleCloseOrderDetails}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: { xs: 1, sm: 2 },
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: isCancelled ? '#C62828' : LOCATION_COLOR }} />
          <Typography variant="h6" component="span" fontWeight={700} sx={{ 
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            color: isCancelled ? '#C62828' : 'inherit',
          }}>
            {isCancelled ? 'Cancelled Order' : 'Order Details'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getStatusChip(orderDetails.order_status, 'order')}
          {getStatusChip(orderDetails.payment_status, 'payment')}
          <IconButton onClick={handleCloseOrderDetails} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {/* If cancelled, show cancellation notice */}
        {isCancelled && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: '10px' }}
            icon={<CancelIcon />}
          >
            This order has been cancelled. No further actions can be taken.
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Order Number
            </Typography>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
              {orderDetails.order_number}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Serial Number
            </Typography>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
              #{String(orderDetails.serial_number || 0).padStart(6, '0')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Date & Time
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              {formatDateTime(orderDetails.order_time)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Location
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              {orderDetails.location_name || LOCATION_NAME}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Order Type
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              {orderDetails.order_type}
              {orderDetails.table_name && ` - Table ${orderDetails.table_name}`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Waiter
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              {orderDetails.waiter_name || localStorage.getItem('user_name') || 'N/A'}
            </Typography>
          </Grid>
          {orderDetails.customer_name && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                Customer
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                {orderDetails.customer_name}
                {orderDetails.customer_phone && ` (${orderDetails.customer_phone})`}
              </Typography>
            </Grid>
          )}
        </Grid>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Order Items ({orderDetails.items?.length || 0})
        </Typography>
        
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${alpha('#000', 0.06)}`, borderRadius: '12px' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(LOCATION_COLOR, 0.05) }}>
                <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderDetails.items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{item.name}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, color: 'text.secondary' }}>{item.category}</TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{item.quantity}</TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{formatCurrency(item.price)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{formatCurrency(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Grid container spacing={1} sx={{ maxWidth: 300 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>Subtotal</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>{formatCurrency(orderDetails.subtotal)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>Tax</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>{formatCurrency(orderDetails.tax)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>Discount</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" color="error" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }}>-{formatCurrency(orderDetails.discount)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Total</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6" fontWeight={700} align="right" color={isCancelled ? '#C62828' : LOCATION_COLOR} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                {isCancelled ? 'CANCELLED' : formatCurrency(orderDetails.total_amount)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        {orderDetails.notes && (
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#FF9800', 0.05), borderRadius: '8px', border: `1px solid ${alpha('#FF9800', 0.2)}` }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Notes</Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>{orderDetails.notes}</Typography>
          </Box>
        )}
        
        {/* If cancelled, also show cancellation note in a special section */}
        {isCancelled && orderDetails.notes?.includes('[CANCELLED') && (
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#F44336', 0.05), borderRadius: '8px', border: `1px solid ${alpha('#F44336', 0.2)}` }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
              Cancellation Info
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
              {orderDetails.notes}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: 3,
        pb: 3,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={handleCloseOrderDetails}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
          }}
        >
          Close
        </Button>
        
        {/* Collect Payment - Only show for non-cancelled, non-paid orders */}
        {!isPaid && !isCancelled && orderDetails?.payment_status !== 'COMPLIMENTARY' && (
          <Button
            variant="contained"
            onClick={() => {
              handleCloseOrderDetails();
              handleOpenPaymentDialog(orderDetails);
            }}
            sx={{
              bgcolor: LOCATION_COLOR,
              '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
            }}
            startIcon={<PaymentIcon />}
          >
            Collect Payment
          </Button>
        )}

        {/* Print Receipt - Only show for paid/complimentary orders that are NOT cancelled */}
        {(isPaid || orderDetails?.payment_status === 'COMPLIMENTARY') && !isCancelled && (
          <Button
            variant="contained"
            onClick={() => {
              handleCloseOrderDetails();
              handlePrintReceipt(orderDetails);
            }}
            sx={{
              bgcolor: orderDetails?.payment_status === 'COMPLIMENTARY' ? '#9C27B0' : '#2E7D32',
              '&:hover': { 
                bgcolor: orderDetails?.payment_status === 'COMPLIMENTARY' ? '#7B1FA2' : '#1B5E20' 
              },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
            }}
            startIcon={<PrintIcon />}
          >
            {orderDetails?.payment_status === 'COMPLIMENTARY' ? 'Comp. Receipt' : 'Print Receipt'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}, [
  orderDetailDialogOpen,
  orderDetails,
  LOCATION_COLOR,
  LOCATION_NAME,
  getStatusChip,
  handleCloseOrderDetails,
  handleOpenPaymentDialog,
  handlePrintReceipt,
]);

  // ============================================
  // RECEIPT DIALOG
  // ============================================
  
  const renderReceiptDialog = useCallback(() => {
    if (!receiptData) return null;
    
    const receiptHTML = generateThermalReceipt(receiptData);
    const isPaid = receiptData.payment_status === 'PAID' || receiptData.payment_status === 'REFUNDED';
    
    return (
      <Dialog
        open={receiptDialogOpen}
        onClose={handleCloseReceiptDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
            maxWidth: '420px',
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: '#f8f9fa',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Payment Receipt
            </Typography>
          </Box>
          <IconButton onClick={handleCloseReceiptDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, bgcolor: '#f5f7fa' }}>
          {isReceiptPrinting && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  Preparing print...
                </Typography>
                <CircularProgress size={20} />
              </Box>
              <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
            </Box>
          )}
          
        {(!isPaid && receiptData?.payment_status !== 'COMPLIMENTARY') ? (
  <Alert
    severity="warning"
    sx={{ mb: 2, borderRadius: '10px' }}
  >
    This order has not been paid yet. Please process payment first.
  </Alert>
) : (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      bgcolor: 'white',
      borderRadius: '8px',
      border: `1px solid ${alpha('#000', 0.06)}`,
    }}
  >
    <div dangerouslySetInnerHTML={{ __html: receiptHTML }} />
  </Paper>
)}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          bgcolor: '#f8f9fa',
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleCloseReceiptDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
         {(isPaid || receiptData?.payment_status === 'COMPLIMENTARY') && (
  <Button
    variant="contained"
    onClick={() => handlePrintReceipt(receiptData)}
    disabled={isReceiptPrinting}
    startIcon={isReceiptPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
    sx={{
      bgcolor: receiptData?.payment_status === 'COMPLIMENTARY' ? '#9C27B0' : '#2E7D32',
      '&:hover': { 
        bgcolor: receiptData?.payment_status === 'COMPLIMENTARY' ? '#7B1FA2' : '#1B5E20' 
      },
      textTransform: 'none',
      fontWeight: 700,
      borderRadius: '8px',
      px: 4,
    }}
  >
    {isReceiptPrinting ? 'Printing...' : (receiptData?.payment_status === 'COMPLIMENTARY' ? 'Print Comp. Receipt' : 'Print Receipt')}
  </Button>
)}
        </DialogActions>
      </Dialog>
    );
  }, [
    receiptDialogOpen,
    receiptData,
    isReceiptPrinting,
    LOCATION_COLOR,
    handleCloseReceiptDialog,
    generateThermalReceipt,
    handlePrintReceipt,
  ]);

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} Cashier...`} size={70} fullScreen={true} />;
  }

  // ============================================
  // IMPORT SIDEBAR DYNAMICALLY
  // ============================================
  
  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/cashier/${locationKey}/Sidebar`).default;
  } catch (e) {
    Sidebar = require('../../../components/RestaurantPOS/cashier/african/Sidebar').default;
  }

  // Pagination
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  
  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          
          <div className="epos-main" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Header />
            
            <Box sx={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Main Content */}
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: { xs: 2, sm: 3 },
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.300',
                  borderRadius: 3,
                },
              }}>
                {/* Hero Section */}
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: '16px',
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 2,
                  border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <Box>
                    <Typography variant="h4" sx={{
                      color: LOCATION_COLOR,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.2rem', sm: '2rem', md: '2.125rem' },
                    }}>
                      {displayText}
                      <span style={{
                        display: 'inline-block',
                        width: '3px',
                        height: { xs: '24px', sm: '28px' },
                        backgroundColor: LOCATION_COLOR,
                        marginLeft: '4px',
                        verticalAlign: 'text-bottom',
                        animation: 'blink 1s step-end infinite',
                      }} />
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setStatsDialogOpen(true)}
                      startIcon={<AssessmentIcon />}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        borderColor: LOCATION_COLOR,
                        color: LOCATION_COLOR,
                        px: 2,
                        py: 0.75,
                        '&:hover': {
                          borderColor: LOCATION_COLOR,
                          bgcolor: alpha(LOCATION_COLOR, 0.08),
                        },
                      }}
                    >
                      Stats
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={fetchOrders}
                      startIcon={<RefreshIcon />}
                      sx={{
                        bgcolor: LOCATION_COLOR,
                        '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        borderRadius: '10px',
                        px: 2,
                        py: 0.75,
                      }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>
                
                {/* Stats Summary */}
                {renderStatsSummary()}
                
                {/* Filters */}
                {renderFilters()}
                
                {/* Orders List */}
                {isFetching ? (
                  <Box sx={{ p: 2 }}>
                    {[...Array(3)].map((_, index) => (
                      <Skeleton key={index} variant="rectangular" height={100} sx={{ mb: 2, borderRadius: '12px' }} />
                    ))}
                  </Box>
                ) : filteredOrders.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 4, sm: 8 },
                      textAlign: 'center',
                      borderRadius: '16px',
                      border: `1px solid ${alpha('#000', 0.06)}`,
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                      No orders found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {searchTerm ? 'Try adjusting your search or filters' : 'No pending orders for today'}
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Box>
                      {paginatedOrders.map((order) => renderOrderCard(order))}
                    </Box>
                    
                    {filteredOrders.length > rowsPerPage && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <TablePagination
                          component="div"
                          count={filteredOrders.length}
                          page={page}
                          onPageChange={(_, newPage) => setPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          sx={{
                            '& .MuiTablePagination-select': {
                              fontSize: { xs: '0.65rem', sm: '0.875rem' },
                            },
                            '& .MuiTablePagination-displayedRows': {
                              fontSize: { xs: '0.6rem', sm: '0.875rem' },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </div>
        </div>
        <Footer />
      </div>
      
      {/* Dialogs */}
      {renderBillDialog()}
      {renderPaymentDialog()}
      {renderOrderDetailDialog()}
      {renderReceiptDialog()}
      
      {/* Stats Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Order Statistics
            </Typography>
          </Box>
          <IconButton onClick={() => setStatsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {stats ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#1976D2', 0.1)}`,
                    bgcolor: alpha('#1976D2', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Total Orders
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1976D2', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {stats.total_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#2E7D32', 0.1)}`,
                    bgcolor: alpha('#2E7D32', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2E7D32', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {formatCurrency(stats.total_revenue || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#C62828', 0.1)}`,
                    bgcolor: alpha('#C62828', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Unpaid Orders
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#C62828', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {stats.unpaid_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#2E7D32', 0.1)}`,
                    bgcolor: alpha('#2E7D32', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Paid Orders
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2E7D32', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {stats.paid_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#E65100', 0.1)}`,
                    bgcolor: alpha('#E65100', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Pending Orders
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#E65100', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {stats.pending_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#4A148C', 0.1)}`,
                    bgcolor: alpha('#4A148C', 0.03),
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                    Completed Orders
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#4A148C', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                    {stats.completed_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
        }}>
          <Button
            onClick={() => setStatsDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default CashierDashboard;